import { hasPermissionToEditFile } from '@/app/actions';
import { debugFlag } from '@/app/debugFlags/debugFlags';
import { events } from '@/app/events/events';
import { sheets } from '@/app/grid/controller/Sheets';
import { MULTIPLAYER_COLORS, MULTIPLAYER_COLORS_TINT } from '@/app/gridGL/HTMLGrid/multiplayerCursor/multiplayerColors';
import { content } from '@/app/gridGL/pixiApp/contentInstance';
import * as gridSettings from '@/app/gridGL/pixiApp/gridSettings';
import { pixiApp } from '@/app/gridGL/pixiApp/PixiApp';
import { JsSelection } from '@/app/quadratic-core/quadratic_core';
import { isPatchVersionDifferent } from '@/app/schemas/compareVersions';
import { RefreshType } from '@/app/shared/types/RefreshType';
import type { SheetPosTS } from '@/app/shared/types/size';
import type { CodeRun } from '@/app/web-workers/CodeRun';
import { aiUser } from '@/app/web-workers/multiplayerWebWorker/aiUser';
import type {
  ClientMultiplayerMessage,
  MultiplayerClientMessage,
  MultiplayerClientUserUpdate,
  MultiplayerState,
} from '@/app/web-workers/multiplayerWebWorker/multiplayerClientMessages';
import type { MultiplayerUser, ReceiveRoom } from '@/app/web-workers/multiplayerWebWorker/multiplayerTypes';
import { quadraticCore } from '@/app/web-workers/quadraticCore/quadraticCore';
import type { User } from '@/auth/auth';
import { authClient } from '@/auth/auth';
import { parseDomain } from '@/auth/auth.helper';
import { VERSION } from '@/shared/constants/appConstants';
import { formatWorkerErrorMessage, isLikelyBrowserAbort } from '@/shared/utils/browserLifecycle';
import { sendAnalyticsError } from '@/shared/utils/error';
import { displayName } from '@/shared/utils/userUtil';
import Color from 'color';
import { v4 as uuid } from 'uuid';

// Initial delay before the first /version.json recheck after the multiplayer
// server reports a different version. Subsequent rechecks use exponential
// backoff (5s, 10s, 20s, 40s, 60s, 60s, ...) capped at RECHECK_VERSION_MAX_MS,
// so an out-of-date tab doesn't poll /version.json every 5s for the rest of
// its lifetime while waiting for the CDN to serve the new bundle.
const RECHECK_VERSION_BASE_MS = 5000;
const RECHECK_VERSION_MAX_MS = 60_000;

export class Multiplayer {
  private worker?: Worker;

  state: MultiplayerState = 'startup';
  sessionId: string;

  private anonymous?: boolean;
  private fileId?: string;
  private jwt?: string | void;

  private codeRunning?: SheetPosTS[];
  private lastMouseMove: { x: number; y: number } | undefined;

  brokenConnection = false;

  // server-assigned index of current user
  index?: number;
  colorString?: string;

  // users currently logged in to the room
  users: Map<string, MultiplayerUser> = new Map();

  private listenersInitialized = false;
  private boundBeforeUnload?: (e: BeforeUnloadEvent) => void;
  private boundOnline?: () => void;
  private boundOffline?: () => void;
  private boundMultiplayerState?: (state: MultiplayerState) => void;
  private versionCheckTimeout?: ReturnType<typeof setTimeout>;

  constructor() {
    this.sessionId = uuid();
    this.initListeners();
  }

  private initListeners() {
    if (this.listenersInitialized) return;
    this.listenersInitialized = true;

    this.boundBeforeUnload = (e: BeforeUnloadEvent) => {
      if (
        this.state === 'syncing' &&
        !this.brokenConnection &&
        hasPermissionToEditFile(gridSettings.getEditorInteractionState().permissions)
      ) {
        e.preventDefault();
      }
    };
    this.boundOnline = () => this.sendOnline();
    this.boundOffline = () => this.sendOffline();
    this.boundMultiplayerState = (state: MultiplayerState) => {
      this.state = state;
    };

    window.addEventListener('beforeunload', this.boundBeforeUnload);
    window.addEventListener('online', this.boundOnline);
    window.addEventListener('offline', this.boundOffline);
    events.on('changeSheet', this.sendChangeSheet);
    events.on('codeRunningState', this.codeState);
    events.on('multiplayerState', this.boundMultiplayerState);
  }

  private removeListeners() {
    if (!this.listenersInitialized) return;
    this.listenersInitialized = false;

    if (this.boundBeforeUnload) {
      window.removeEventListener('beforeunload', this.boundBeforeUnload);
    }
    if (this.boundOnline) {
      window.removeEventListener('online', this.boundOnline);
    }
    if (this.boundOffline) {
      window.removeEventListener('offline', this.boundOffline);
    }
    if (this.boundMultiplayerState) {
      events.off('multiplayerState', this.boundMultiplayerState);
    }
    events.off('changeSheet', this.sendChangeSheet);
    events.off('codeRunningState', this.codeState);
  }

  private sendAnalyticsError = (
    from: string,
    error: Error | unknown,
    options?: { extra?: Record<string, unknown>; treatAsNetworkFailure?: boolean }
  ) => {
    sendAnalyticsError('multiplayer', from, error, undefined, options);
  };

  initWorker() {
    if (!this.worker) {
      this.worker = new Worker(new URL('./worker/multiplayer.worker.ts', import.meta.url), { type: 'module' });
      this.worker.onmessage = this.handleMessage;
      this.worker.onerror = (e) => {
        const formatted = formatWorkerErrorMessage(e, 'Multiplayer worker error (no details available)');

        // Browser-induced teardown (Memory Saver, tab discard, freeze, offline)
        // reliably produces a no-details `ErrorEvent`. Skip Sentry capture in
        // that case — there's nothing actionable for us to do. Real worker-load
        // failures (visible online tab) still flow through `sendAnalyticsError`.
        if (isLikelyBrowserAbort(e)) {
          console.warn(`[multiplayer] initWorker (suppressed, browser-induced abort): ${formatted}`, e);
          return;
        }

        const noDetails = !e.message;
        this.sendAnalyticsError('initWorker', new Error(formatted), {
          extra: noDetails ? { noWorkerErrorDetails: true } : undefined,
          treatAsNetworkFailure: noDetails,
        });
      };
    }
  }

  private codeState = (current?: CodeRun, awaitingExecution?: CodeRun[]) => {
    const codeRunning: SheetPosTS[] = [];
    if (current) {
      codeRunning.push(current.sheetPos);
    }
    if (awaitingExecution?.length) {
      codeRunning.push(...awaitingExecution.map((cell) => cell.sheetPos));
    }
    // Update local state and send to multiplayer
    this.codeRunning = codeRunning;
    if (this.fileId) {
      // Only send if multiplayer is initialized
      this.sendCodeRunning(codeRunning);
    }
  };

  private handleMessage = async (e: MessageEvent<MultiplayerClientMessage>) => {
    if (debugFlag('debugWebWorkersMessages')) console.log(`[Multiplayer] message: ${e.data.type}`);

    switch (e.data.type) {
      case 'multiplayerClientState':
        this.state = e.data.state;
        if (this.state === 'no internet' || this.state === 'waiting to reconnect') {
          this.clearAllUsers();
          this.brokenConnection = true;
        } else if (this.state === 'connected') {
          this.brokenConnection = false;
        }
        events.emit('multiplayerState', this.state);
        break;

      case 'multiplayerClientUserUpdate':
        this.receiveUserUpdate(e.data);
        break;

      case 'multiplayerClientUsersInRoom':
        await this.receiveUsersInRoom(e.data.room);
        break;

      case 'multiplayerClientReload':
        events.emit('needRefresh', RefreshType.FORCE);
        break;

      case 'multiplayerClientRefreshJwt':
        await this.addJwtCookie(true, Boolean(this.anonymous));
        this.send({ type: 'clientMultiplayerRefreshJwt', id: e.data.id });
        break;

      default:
        console.warn('Unhandled message type', e.data);
    }
  };

  private send(message: ClientMultiplayerMessage, port?: MessagePort) {
    if (!this.worker) {
      throw new Error('Expected Worker to be initialized in multiplayer.send');
    }
    if (port) {
      this.worker.postMessage(message, [port]);
    } else {
      this.worker.postMessage(message);
    }
  }

  private async getJwt(skipRedirect?: boolean) {
    const token = await authClient.getTokenOrRedirect(skipRedirect);
    // Only update this.jwt if we got a valid token
    // getTokenOrRedirect may return empty string if session is expired
    if (token) {
      this.jwt = token;
    } else {
      this.jwt = undefined;
    }
  }

  private async addJwtCookie(force: boolean = false, skipRedirect: boolean = false) {
    if (force || !this.jwt) {
      await this.getJwt(skipRedirect);

      if (this.jwt) {
        let domain = parseDomain(window.location.host);
        // SameSite=None requires Secure flag, which only works over HTTPS.
        // For localhost development (HTTP), use SameSite=Lax instead.
        // Safari strictly enforces this while Chrome is more lenient in dev.
        const isSecure = window.location.protocol === 'https:';
        const sameSiteAttr = isSecure ? 'SameSite=None; Secure;' : 'SameSite=Lax;';
        document.cookie = `jwt=${this.jwt}; ${sameSiteAttr} path=/; domain=${domain}`;
      }
    }
  }

  async init(fileId: string, user: User, anonymous: boolean) {
    // Skip if already connected/syncing for this same file
    if ((this.state === 'connected' || this.state === 'syncing') && this.fileId === fileId) {
      return;
    }

    // Wait for sheets to be initialized before connecting.
    // This can happen if multiplayer.init() is called before the sheetsInfo
    // message from core worker has been processed.
    if (!sheets.initialized) {
      await new Promise<void>((resolve) => {
        const checkSheets = () => {
          if (sheets.initialized) {
            events.off('sheetsInfo', checkSheets);
            resolve();
          }
        };
        events.on('sheetsInfo', checkSheets);
        // Also check immediately in case it was just initialized
        checkSheets();
      });

      // After waiting, verify we still want to connect to this file
      // (another init call might have come in while waiting)
      if (this.fileId && this.fileId !== fileId) {
        return;
      }
    }

    // Ensure worker exists
    this.initWorker();

    // If switching to a different file, the connection pool in the worker handles
    // releasing the old connection (with draining if pending transactions exist)
    if (this.fileId && this.fileId !== fileId) {
      if (debugFlag('debugShowMultiplayer')) {
        console.log(`[Multiplayer] init() switching from ${this.fileId} to ${fileId}`);
      }
      // Clear local user state for the old file
      this.clearAllUsers();
    }

    // channel for communication between the quadraticCore and the multiplayer web worker
    const channel = new MessageChannel();

    this.fileId = fileId;
    this.anonymous = anonymous;
    if (!this.anonymous) {
      await this.addJwtCookie(true);
    }

    // After async JWT operation, verify we're still initializing for the same file
    if (this.fileId !== fileId) {
      if (debugFlag('debugShowMultiplayer')) {
        console.log(`[Multiplayer] init() aborted for ${fileId}, fileId changed to ${this.fileId}`);
      }
      return;
    }

    this.send(
      {
        type: 'clientMultiplayerInit',
        fileId,
        user,
        anonymous,
        sessionId: this.sessionId,
        sheetId: sheets.current,
        selection: sheets.getMultiplayerSelection(),
        cellEdit: {
          text: '',
          cursor: 0,
          active: false,
          code_editor: false,
          inline_code_editor: false,
        },
        viewport: pixiApp.saveMultiplayerViewport(),
        codeRunning: JSON.stringify(this.codeRunning),
        follow: gridSettings.getEditorInteractionState().follow,
      },
      channel.port1
    );
    quadraticCore.initMultiplayer(channel.port2);
  }

  // used to pre-populate useMultiplayerUsers.tsx
  getUsers(): MultiplayerUser[] {
    return Array.from(this.users.values());
  }

  // whether a multiplayer user is already editing a cell
  cellIsBeingEdited(x: number, y: number, sheetId: string): { codeEditor: boolean; user: string } | undefined {
    for (const player of this.users.values()) {
      if (player.sheet_id === sheetId && player.cell_edit.active && player.parsedSelection) {
        const cursor = player.parsedSelection.getCursor();
        if (cursor.x === x && cursor.y === y) {
          const user = displayName(player, false);
          return { codeEditor: player.cell_edit.code_editor, user };
        }
      }
    }
  }

  sendMouseMove(x?: number, y?: number) {
    if (!this.worker) return;
    if (this.lastMouseMove === undefined && x === undefined) return;
    if (this.lastMouseMove && this.lastMouseMove.x === x && this.lastMouseMove.y === y) return;
    this.lastMouseMove = x === undefined || y === undefined ? undefined : { x, y };
    const visible = x !== undefined && y !== undefined;
    this.send({ type: 'clientMultiplayerMouseMove', x, y, visible });
  }

  async sendSelection(selection: string) {
    if (!this.worker) return;
    this.send({ type: 'clientMultiplayerSelection', selection });
  }

  private sendChangeSheet = () => {
    if (!this.worker) return;
    this.send({ type: 'clientMultiplayerSheet', sheetId: sheets.current });
  };

  private sendOnline = () => {
    // don't send this event if we not in app, if not initialized yet
    if (this.fileId) {
      this.send({ type: 'clientMultiplayerOnline' });
    }
  };

  private sendOffline = () => {
    // don't send this event if we not in app, if not initialized yet
    if (this.fileId) {
      this.send({ type: 'clientMultiplayerOffline' });
    }
  };

  sendCellEdit(options: {
    text: string;
    cursor: number;
    codeEditor: boolean;
    inlineCodeEditor: boolean;
    bold?: boolean;
    italic?: boolean;
    underline?: boolean;
    strikeThrough?: boolean;
  }) {
    if (!this.worker) return;
    const { text, cursor, codeEditor, inlineCodeEditor, bold, italic, underline, strikeThrough } = options;
    this.send({
      type: 'clientMultiplayerCellEdit',
      cellEdit: {
        text,
        cursor,
        active: true,
        code_editor: codeEditor,
        inline_code_editor: inlineCodeEditor,
        bold,
        italic,
        underline,
        strikeThrough,
      },
    });
  }

  sendEndCellEdit() {
    if (!this.worker) return;
    this.send({ type: 'clientMultiplayerCellEdit' });
  }

  sendViewport(viewport: string) {
    if (!this.worker) return;
    this.send({ type: 'clientMultiplayerViewport', viewport });
  }

  sendCodeRunning(sheetPos: SheetPosTS[]) {
    if (!this.worker) return;
    this.send({ type: 'clientMultiplayerCodeRunning', sheetPos: JSON.stringify(sheetPos) });
  }

  sendFollow(follow: string) {
    if (!this.worker) return;
    this.send({ type: 'clientMultiplayerFollow', follow });
  }

  setAIUser(isActive: boolean) {
    const aiSessionId = 'ai-analyst';
    // Use purple color for AI
    const aiColorString = '#a855f7'; // Purple color
    const aiColor = Color(aiColorString).rgbNumber();

    if (isActive) {
      // Add AI user if not already present
      if (!this.users.has(aiSessionId)) {
        // Get the top-left cell (0, 0) position in world coordinates
        const topLeftCell = sheets.sheet.getCellOffsets(0, 0);
        const x = topLeftCell.x + topLeftCell.width / 2; // Center of the cell
        const y = topLeftCell.y + topLeftCell.height / 2; // Center of the cell

        const aiUser: MultiplayerUser = {
          session_id: aiSessionId,
          file_id: this.fileId || '',
          user_id: 'ai-analyst',
          first_name: 'Quadratic',
          last_name: 'AI',
          email: 'ai@quadratic.ai',
          image: '/logo192.png',
          sheet_id: sheets.current,
          selection: undefined,
          parsedSelection: undefined,
          cell_edit: {
            active: false,
            text: '',
            cursor: 0,
            code_editor: false,
            inline_code_editor: false,
          },
          x,
          y,
          color: aiColor,
          colorString: aiColorString,
          visible: true,
          index: 999,
          viewport: '{}',
          code_running: '',
          parsedCodeRunning: [],
          follow: undefined,
        };
        this.users.set(aiSessionId, aiUser);
        events.emit('multiplayerUpdate', this.getUsers());
        events.emit('setDirty', { multiplayerCursor: true });
        events.emit('multiplayerCursor');
      }
    } else {
      // Remove AI user if present
      if (this.users.has(aiSessionId)) {
        // Stop any ongoing animation
        aiUser.stopAnimation();

        this.users.delete(aiSessionId);
        events.emit('multiplayerUpdate', this.getUsers());
        events.emit('setDirty', { multiplayerCursor: true });
        events.emit('multiplayerCursor');
      }
    }
  }

  updateAIUserPosition(x?: number, y?: number, visible?: boolean, sheetId?: string) {
    const aiSessionId = 'ai-analyst';
    const player = this.users.get(aiSessionId);
    if (!player) return;

    let needsUpdate = false;

    if (x !== undefined && y !== undefined) {
      player.x = x;
      player.y = y;
      needsUpdate = true;
    }

    if (visible !== undefined) {
      player.visible = visible;
      needsUpdate = true;
    }

    if (sheetId !== undefined) {
      const sheetChanged = player.sheet_id !== sheetId;
      player.sheet_id = sheetId;
      if (sheetChanged) {
        events.emit('multiplayerChangeSheet');
      }
      needsUpdate = true;
    }

    if (needsUpdate) {
      if (player.sheet_id === sheets.current) {
        events.emit('setDirty', { multiplayerCursor: true });
        events.emit('multiplayerCursor');
      }
      events.emit('multiplayerUpdate', this.getUsers());
    }
  }

  private clearAllUsers() {
    if (debugFlag('debugShowMultiplayer')) console.log('[Multiplayer] Clearing all users.');
    // Preserve AI user if it exists (it's managed client-side)
    const aiUser = this.users.get('ai-analyst');
    this.users.clear();
    if (aiUser) {
      this.users.set('ai-analyst', aiUser);
    }
    events.emit('setDirty', { multiplayerCursor: true });
    events.emit('multiplayerUpdate', this.getUsers());
    events.emit('multiplayerChangeSheet');
    events.emit('multiplayerCursor');
  }

  private receiveUserUpdate(userUpdate: MultiplayerClientUserUpdate) {
    const player = this.users.get(userUpdate.sessionId);

    // it's possible we get the UserUpdate before the EnterRoom response. No big deal if we do.
    if (!player) return;

    // Ignore updates for a different file (can happen during rapid file switching)
    if (userUpdate.fileId !== this.fileId) {
      if (debugFlag('debugShowMultiplayer')) {
        console.log(`[Multiplayer] Ignoring UserUpdate for file ${userUpdate.fileId}, current file is ${this.fileId}`);
      }
      return;
    }
    const update = userUpdate.userUpdate;
    if (update.x !== null && update.y !== null) {
      player.x = update.x;
      player.y = update.y;
      if (player.sheet_id === sheets.current) {
        events.emit('multiplayerCursor');
      }
    }

    if (update.visible !== undefined) {
      player.visible = update.visible;
    }

    if (update.sheet_id) {
      if (player.sheet_id !== update.sheet_id) {
        player.sheet_id = update.sheet_id;
        events.emit('multiplayerChangeSheet');
        if (player.sheet_id === sheets.current) {
          events.emit('setDirty', { multiplayerCursor: true });
          events.emit('multiplayerCursor');
        }
      }
    }

    if (update.selection) {
      player.selection = update.selection;
      player.parsedSelection = new JsSelection(player.sheet_id);
      if (player.selection) {
        player.parsedSelection.load(player.selection);
      }
      if (player.sheet_id === sheets.current) {
        events.emit('setDirty', { multiplayerCursor: true });
      }
    }

    if (update.cell_edit) {
      player.cell_edit = update.cell_edit;
      if (!update.cell_edit.code_editor) {
        if (player.parsedSelection) {
          // hide the label if the player is editing the cell
          const cursor = player.parsedSelection.getCursor();
          content.cellsSheets.showLabel(cursor.x, cursor.y, player.sheet_id, !player.cell_edit.active);
        }
        events.emit('multiplayerCellEdit', update.cell_edit, player);
      }
      events.emit('setDirty', { multiplayerCursor: true });
    }

    if (update.viewport) {
      player.viewport = update.viewport;
      if (gridSettings.getEditorInteractionState().follow === player.session_id) {
        pixiApp.viewport.loadMultiplayerViewport(JSON.parse(player.viewport));
      }
    }

    if (update.code_running) {
      player.code_running = update.code_running;
      player.parsedCodeRunning = JSON.parse(update.code_running);

      // trigger changes in CodeRunning.tsx
      events.emit('multiplayerCodeRunning', player);
    }

    if (update.follow !== null) {
      player.follow = update.follow;
      events.emit('multiplayerFollow');
    }
  }

  private async checkVersion(serverVersion: string, attempt: number = 0) {
    if (serverVersion === VERSION) {
      return;
    }

    // Externally-initiated checks (attempt 0, e.g. from receiveUsersInRoom)
    // cancel any in-flight retry chain so we don't end up running multiple
    // polling chains in parallel and so backoff resets cleanly.
    if (attempt === 0 && this.versionCheckTimeout) {
      clearTimeout(this.versionCheckTimeout);
      this.versionCheckTimeout = undefined;
    }

    if (debugFlag('debugShowVersionCheck')) {
      console.log(`Multiplayer server version (${serverVersion}) is different than the client version (${VERSION})`);
    }

    const nextDelayMs = Math.min(RECHECK_VERSION_BASE_MS * 2 ** attempt, RECHECK_VERSION_MAX_MS);
    const scheduleRecheck = () => {
      this.versionCheckTimeout = setTimeout(() => this.checkVersion(serverVersion, attempt + 1), nextDelayMs);
    };

    try {
      const versionClientJson = await fetch('/version.json').then((res) => res.json());
      if (typeof versionClientJson !== 'object' || !('version' in versionClientJson)) {
        throw new Error(`Invalid version.json: ${JSON.stringify(versionClientJson)}`);
      }
      const versionClient = versionClientJson.version;

      // we may have to wait to show the update dialog if the client version
      // on the server is different than the one served from the client
      if (versionClient === serverVersion) {
        events.emit(
          'needRefresh',
          isPatchVersionDifferent(versionClient, VERSION) ? RefreshType.RECOMMENDED : RefreshType.REQUIRED
        );
      } else {
        if (debugFlag('debugShowVersionCheck')) {
          console.log(
            `quadratic-client's version (${versionClient}) does not yet match the quadratic-multiplayer's version (${serverVersion}) (trying again in ${nextDelayMs}ms)`
          );
        }
        scheduleRecheck();
      }
    } catch (e) {
      console.warn('[multiplayer.ts] checkVersion: Failed to fetch /version.json file', e);
      scheduleRecheck();
    }
  }

  // updates the React hook to populate the Avatar list
  private async receiveUsersInRoom(room: ReceiveRoom) {
    const remaining = new Set(this.users.keys());
    for (const user of room.users) {
      if (user.session_id === this.sessionId) {
        this.index = user.index;
        this.colorString = MULTIPLAYER_COLORS[user.index % MULTIPLAYER_COLORS.length];
      } else {
        let player = this.users.get(user.session_id);
        const parsedSelection = new JsSelection(user.sheet_id);
        if (user.selection) {
          parsedSelection.load(user.selection);
        }
        if (player) {
          player.first_name = user.first_name;
          player.last_name = user.last_name;
          player.image = user.image;
          player.sheet_id = user.sheet_id;
          player.selection = user.selection;
          player.parsedSelection = parsedSelection;
          remaining.delete(user.session_id);
          if (debugFlag('debugShowMultiplayer')) console.log(`[Multiplayer] Updated player ${user.first_name}.`);
        } else {
          player = {
            session_id: user.session_id,
            file_id: user.file_id,
            user_id: user.user_id,
            first_name: user.first_name,
            last_name: user.last_name,
            email: user.email,
            image: user.image,
            sheet_id: user.sheet_id,
            selection: user.selection,
            parsedSelection,
            cell_edit: user.cell_edit,
            x: 0,
            y: 0,
            color: MULTIPLAYER_COLORS_TINT[user.index % MULTIPLAYER_COLORS_TINT.length],
            colorString: MULTIPLAYER_COLORS[user.index % MULTIPLAYER_COLORS.length],
            visible: false,
            index: user.index,
            viewport: user.viewport,
            code_running: user.code_running,
            parsedCodeRunning: user.code_running ? JSON.parse(user.code_running) : [],
            follow: user.follow,
          };
          this.users.set(user.session_id, player);
          if (debugFlag('debugShowMultiplayer')) console.log(`[Multiplayer] Player ${user.first_name} entered room.`);
        }
      }
    }
    remaining.forEach((sessionId) => {
      // Don't remove the AI user - it's managed client-side
      if (sessionId === 'ai-analyst') return;
      if (debugFlag('debugShowMultiplayer'))
        console.log(`[Multiplayer] Player ${this.users.get(sessionId)?.first_name} left room.`);
      this.users.delete(sessionId);
    });
    events.emit('multiplayerUpdate', this.getUsers());
    events.emit('setDirty', { multiplayerCursor: true });
    events.emit('multiplayerCursor');

    await this.checkVersion(room.version);
  }

  /**
   * Disconnects from the current room without terminating the worker.
   * Used when switching between files in the SPA.
   */
  disconnect() {
    if (!this.worker) return;

    // Clear any pending version check timeout from previous file
    if (this.versionCheckTimeout) {
      clearTimeout(this.versionCheckTimeout);
      this.versionCheckTimeout = undefined;
    }

    this.send({ type: 'clientMultiplayerDisconnect' });
    this.clearAllUsers();
    this.fileId = undefined;
    this.state = 'startup';
  }

  /**
   * Resets local multiplayer state to initial values.
   * Does not affect the worker.
   */
  private resetLocalState() {
    // Clear any pending version check timeout from previous file
    if (this.versionCheckTimeout) {
      clearTimeout(this.versionCheckTimeout);
      this.versionCheckTimeout = undefined;
    }

    this.clearAllUsers();
    this.fileId = undefined;
    this.state = 'startup';
    events.emit('multiplayerState', this.state);
    this.jwt = undefined;
    this.codeRunning = undefined;
    this.lastMouseMove = undefined;
    this.brokenConnection = false;
    this.index = undefined;
    this.colorString = undefined;
    this.sessionId = uuid();
  }

  /**
   * Prepares for switching to a new file.
   * Clears local user state but keeps the worker alive.
   * The connection pool in the worker handles graceful connection release.
   */
  switchFile() {
    // Clear any pending version check timeout from previous file
    if (this.versionCheckTimeout) {
      clearTimeout(this.versionCheckTimeout);
      this.versionCheckTimeout = undefined;
    }

    // Clear users from the old file
    this.clearAllUsers();

    // Reset local state for the new file
    this.fileId = undefined;
    this.state = 'startup';
    events.emit('multiplayerState', this.state);
    this.codeRunning = undefined;
    this.lastMouseMove = undefined;
    this.brokenConnection = false;
    this.index = undefined;
    this.colorString = undefined;

    // Generate new session ID for the new file
    this.sessionId = uuid();
  }

  /**
   * Restarts the multiplayer worker entirely.
   * Terminates the existing worker and creates a fresh one.
   * This is a hard reset - use switchFile() for normal file switching.
   */
  restart() {
    // Terminate existing worker
    if (this.worker) {
      this.worker.terminate();
      this.worker = undefined;
    }

    // Reset local state
    this.resetLocalState();

    // Create fresh worker
    this.initWorker();
  }

  /**
   * Terminates the multiplayer worker entirely.
   * Used when unloading the application.
   */
  terminate() {
    this.removeListeners();
    if (this.worker) {
      this.worker.terminate();
      this.worker = undefined;
    }
    this.resetLocalState();
  }

  /**
   * Returns whether the worker is currently initialized.
   */
  isInitialized(): boolean {
    return !!this.worker;
  }
}

export const multiplayer = new Multiplayer();
